import posthog from "posthog-js";
import { useEffect } from "react";

import { useProject } from "api/hooks/useProject";
import { useProjectId } from "api/hooks/useProjectId";
import { useUser } from "api/hooks/useUser";
import { getThemeAnalyticsProperties } from "theme/themePreference";
import {
  getFromStorage,
  NEW_USER_DATA,
  STORAGE_TYPE,
} from "utils/localStorage";

type UtmParams = { utm_campaign?: string };

/**
 * Identifies the signed-in user and groups them by the current project in
 * PostHog. It runs again when the user or the project changes. Render it only
 * for signed-in users.
 *
 * Reassess it in Phase 4 of the data fetching migration. It could become a
 * `useAnalyticsIdentity()` hook that `Root` calls. That needs a `skip` option
 * on `useUser`, so that signed-out users do not fetch `GET_USER`.
 */
export const AnalyticsIdentity = () => {
  const { user } = useUser();
  const {
    project,
    loading: isQueryLoading,
    error: projectError,
  } = useProject();
  const activeProjectId = useProjectId();

  // Routes without a project in the URL have no project id until a project
  // is selected, or created for a new user. The project query is skipped
  // until then, so it does not load.
  const isProjectPending = isQueryLoading || !activeProjectId;

  const hasProjectError = Boolean(projectError);

  const userId = user?.id;
  const email = user?.email;
  const projectId = project?.id;
  const projectName = project?.name;
  const projectCreated = project?.created;
  const planType = project?.entitlementSpec?.plan_type;

  useEffect(() => {
    // Wait for the project. A project switch then sends one identify call with
    // the new project, not a call without it first.
    // Send nothing when the project fails to load.
    if (!userId || !email || isProjectPending || hasProjectError) return;

    const { utm_campaign } = getFromStorage<UtmParams>(
      "utm_params",
      NEW_USER_DATA,
      {},
      STORAGE_TYPE.LOCAL,
    );

    try {
      if (projectId) {
        posthog.group("project", projectId, {
          project_id: projectId,
          project_name: projectName,
          project_created: projectCreated,
          plan_type: planType,
        });
      }
      posthog.identify(userId, {
        email,
        userId,
        utm_campaign,
        ...getThemeAnalyticsProperties(),

        // NOTE: These properties are old. They change when the user switches projects.
        // Use the group properties above.
        project_created: projectCreated,
        planType,
      });
    } catch (error) {
      console.warn("Failed to update PostHog identity:", error);
    }
  }, [
    userId,
    email,
    isProjectPending,
    hasProjectError,
    projectId,
    projectName,
    projectCreated,
    planType,
  ]);

  return null;
};
